import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings, Shield, Bell, Palette, Key, Smartphone, Globe, Clock, Sun, CheckCircle, Save, Eye, EyeOff, Mail, Phone } from "lucide-react";
import { parametresAPI, authAPI } from "../api/services";
import { saveTimezone } from "../utils/dateUtils";
import { useTheme } from "../context/ThemeContext";
import { langMap } from "../i18n";

// ── Toggle component ──────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-12 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${value ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${value ? "translate-x-[26px]" : "translate-x-0.5"}`} />
    </button>
  );
}

// ── Select style ─────────────────────────────────────────────
const sel = "w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-slate-700 dark:text-slate-200 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none";

// ── Section header ────────────────────────────────────────────
function SectionHeader({ icon: Icon, iconBg, iconColor, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
      <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon size={22} className={iconColor} />
      </div>
      <div>
        <p className="font-bold text-slate-800 dark:text-white">{title}</p>
        <p className="text-xs text-slate-400">{subtitle}</p>
      </div>
    </div>
  );
}

// ── Toggle row ────────────────────────────────────────────────
function ToggleRow({ icon: Icon, iconColor, label, sub, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-gray-50 dark:border-gray-700 last:border-0">
      <div className="flex items-center gap-3">
        {Icon && <Icon size={16} className={iconColor || "text-slate-400"} />}
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

export default function Parametres() {
  const { t, i18n } = useTranslation();
  const { theme: currentTheme, setTheme: applyTheme } = useTheme();

  const card = "bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 transition-colors";

  // Loading
  const [loading, setLoading] = useState(true);

  // Securite
  const [mdpActuel, setMdpActuel]   = useState("");
  const [mdpNouveau, setMdpNouveau] = useState("");
  const [mdpConfirm, setMdpConfirm] = useState("");
  const [showMdp, setShowMdp]       = useState(false);
  const [mdpMsg, setMdpMsg]         = useState("");
  const [mdpIsError, setMdpIsError] = useState(false);

  // Notifications
  const [emailNotif, setEmailNotif]       = useState("");
  const [canalEmail, setCanalEmail]       = useState(true);
  const [canalSMS, setCanalSMS]           = useState(false);
  const [canalPush, setCanalPush]         = useState(true);
  const [alertCritique, setAlertCritique] = useState(true);
  const [alertAvert, setAlertAvert]       = useState(true);
  const [alertInfo, setAlertInfo]         = useState(false);

  // Preferences
  const [langue, setLangue]     = useState("Francais");
  const [fuseau, setFuseau]     = useState("Europe/Paris (GMT+1)");
  const [theme, setTheme]       = useState("Clair");
  const [prefixeTelephone, setPrefixeTelephone] = useState("+216");

  // Saved banner
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Apply language change immediately
  const handleLangueChange = (val) => {
    setLangue(val);
    const code = langMap[val] || 'fr';
    i18n.changeLanguage(code);
    localStorage.setItem('langue', code);
  };

  // Apply timezone change immediately
  const handleFuseauChange = (val) => {
    setFuseau(val);
    saveTimezone(val);
  };

  // Apply theme change immediately
  const handleThemeChange = (val) => {
    setTheme(val);
    applyTheme(val);
  };

  // Charger les parametres depuis l'API
  useEffect(() => {
    const load = async () => {
      try {
        const paramRes = await parametresAPI.get();

        const p = paramRes.data?.data || paramRes.data;
        if (p) {
          if (p.notifications) {
            setEmailNotif(p.notifications.emailNotif || "");
            setCanalEmail(p.notifications.canalEmail ?? true);
            setCanalSMS(p.notifications.canalSMS ?? false);
            setCanalPush(p.notifications.canalPush ?? true);
            setAlertCritique(p.notifications.alertCritique ?? true);
            setAlertAvert(p.notifications.alertAvert ?? true);
            setAlertInfo(p.notifications.alertInfo ?? false);
          }
          if (p.preferences) {
            const lng = p.preferences.langue || "Francais";
            setLangue(lng);
            const code = langMap[lng] || 'fr';
            i18n.changeLanguage(code);
            localStorage.setItem('langue', code);

            const tz = p.preferences.fuseau || "Europe/Paris (GMT+1)";
            setFuseau(tz);
            saveTimezone(tz);

            const th = p.preferences.theme || "Clair";
            setTheme(th);
            applyTheme(th);

            setPrefixeTelephone(p.preferences.prefixeTelephone || "+216");
          }
        }

      } catch (err) {
        console.error("Erreur chargement parametres:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sauvegarder
  const handleSave = async () => {
    setSaving(true);
    try {
      await parametresAPI.update({
        notifications: { emailNotif, canalEmail, canalSMS, canalPush, alertCritique, alertAvert, alertInfo },
        preferences: { langue, fuseau, theme, prefixeTelephone }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
    } finally {
      setSaving(false);
    }
  };

  // Changer mot de passe
  const handleChangePassword = async () => {
    if (!mdpActuel || !mdpNouveau) return;
    if (mdpNouveau !== mdpConfirm) {
      setMdpMsg(t('settings.security.passwordMismatch'));
      setMdpIsError(true);
      return;
    }
    try {
      await authAPI.updatePassword(mdpActuel, mdpNouveau);
      setMdpMsg(t('settings.security.passwordUpdated'));
      setMdpIsError(false);
      setMdpActuel(""); setMdpNouveau(""); setMdpConfirm("");
    } catch (err) {
      setMdpMsg(err.response?.data?.error || t('settings.security.passwordError'));
      setMdpIsError(true);
    }
  };

  const inputPwd = (val, setVal) => (
    <div className="relative">
      <input type={showMdp ? "text" : "password"} value={val} onChange={e => setVal(e.target.value)}
        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-11" />
      <button type="button" onClick={() => setShowMdp(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
        {showMdp ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</span>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-slate-600 dark:text-slate-300" />
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">{t('settings.title')}</h1>
          </div>
          <p className="text-sm text-slate-400 mt-0.5 ml-7">{t('settings.subtitle')}</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-xl text-sm text-green-700 dark:text-green-400 font-medium">
            <CheckCircle size={15} /> {t('common.saved')}
          </div>
        )}
      </div>

      {/* Ligne 1 : Securite + Notifications */}
      <div className="grid grid-cols-2 gap-5 mb-5">

        {/* Securite */}
        <div className={card}>
          <SectionHeader icon={Shield} iconBg="bg-red-500" iconColor="text-white" title={t('settings.security.title')} subtitle={t('settings.security.subtitle')} />

          <div className="border border-gray-100 dark:border-gray-700 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Key size={15} className="text-slate-500 dark:text-slate-400" />
              <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm">{t('settings.security.changePassword')}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block">{t('settings.security.currentPassword')}</label>
                {inputPwd(mdpActuel, setMdpActuel)}
              </div>
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block">{t('settings.security.newPassword')}</label>
                {inputPwd(mdpNouveau, setMdpNouveau)}
              </div>
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 block">{t('settings.security.confirmPassword')}</label>
                {inputPwd(mdpConfirm, setMdpConfirm)}
              </div>
              {mdpMsg && <p className={`text-xs ${mdpIsError ? "text-red-500" : "text-green-600"}`}>{mdpMsg}</p>}
              <button onClick={handleChangePassword}
                className="w-full py-3 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 flex items-center justify-center gap-2">
                <Shield size={15} /> {t('settings.security.updatePassword')}
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className={card}>
          <SectionHeader icon={Bell} iconBg="bg-blue-500" iconColor="text-white" title={t('settings.notifications.title')} subtitle={t('settings.notifications.subtitle')} />

          <div className="border border-gray-100 dark:border-gray-700 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={14} className="text-slate-400" />
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.notifications.mainEmail')}</p>
            </div>
            <input value={emailNotif} onChange={e => setEmailNotif(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm bg-gray-50 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="border border-gray-100 dark:border-gray-700 rounded-xl p-4 mb-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">{t('settings.notifications.channels')}</p>
            <ToggleRow icon={Mail}       iconColor="text-slate-400" label={t('settings.notifications.email')}  sub={t('settings.notifications.emailSub')}  value={canalEmail} onChange={setCanalEmail} />
            <ToggleRow icon={Smartphone} iconColor="text-slate-400" label={t('settings.notifications.sms')}    sub={t('settings.notifications.smsSub')}    value={canalSMS}   onChange={setCanalSMS} />
            <ToggleRow icon={Bell}       iconColor="text-slate-400" label={t('settings.notifications.push')}   sub={t('settings.notifications.pushSub')}   value={canalPush}  onChange={setCanalPush} />
          </div>

          <div className="border border-gray-100 dark:border-gray-700 rounded-xl p-4 mb-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">{t('settings.notifications.alertTypes')}</p>
            <div className="space-y-1">
              {[
                { dot: "bg-red-500",    label: t('settings.notifications.critical'),  sub: t('settings.notifications.criticalSub'),  key: "critique" },
                { dot: "bg-yellow-500", label: t('settings.notifications.warnings'),  sub: t('settings.notifications.warningSub'),   key: "avert" },
                { dot: "bg-blue-500",   label: t('settings.notifications.info'),      sub: t('settings.notifications.infoSub'),      key: "info" },
              ].map((r) => {
                const checked = r.key === "critique" ? alertCritique : r.key === "avert" ? alertAvert : alertInfo;
                return (
                  <label key={r.key} className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-gray-700 last:border-0 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full ${r.dot} flex-shrink-0`} />
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{r.label}</p>
                        <p className="text-xs text-slate-400">{r.sub}</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="alertType"
                      checked={checked}
                      onChange={() => {
                        setAlertCritique(r.key === "critique");
                        setAlertAvert(r.key === "avert");
                        setAlertInfo(r.key === "info");
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                  </label>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {/* Ligne 2 : Preferences */}
      <div className="mb-5">
        <div className={card}>
          <SectionHeader icon={Palette} iconBg="bg-purple-500" iconColor="text-white" title={t('settings.preferences.title')} subtitle={t('settings.preferences.subtitle')} />
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: Globe, label: t('settings.preferences.language'),   val: langue,     handler: handleLangueChange,        options: [
                { value: "Francais", label: "Français" },
                { value: "English", label: "English" },
                { value: "Espanol", label: "Español" },
              ] },
              { icon: Clock, label: t('settings.preferences.timezone'),   val: fuseau,     handler: handleFuseauChange,          options: [
                { value: "Europe/Paris (GMT+1)", label: "Europe/Paris (GMT+1)" },
                { value: "Africa/Tunis (GMT+1)", label: "Africa/Tunis (GMT+1)" },
                { value: "America/New_York (GMT-5)", label: "America/New_York (GMT-5)" },
                { value: "Asia/Dubai (GMT+4)", label: "Asia/Dubai (GMT+4)" },
              ] },
              { icon: Sun,   label: t('settings.preferences.theme'),      val: theme,      handler: handleThemeChange,           options: [
                { value: "Clair", label: t('settings.preferences.themeLight') },
                { value: "Sombre", label: t('settings.preferences.themeDark') },
                { value: "Automatique", label: t('settings.preferences.themeAuto') },
              ] },
              { icon: Phone, label: "Préfixe téléphone", val: prefixeTelephone, handler: setPrefixeTelephone, options: [
                { value: "+216", label: "Tunisie (+216)" },
                { value: "+33",  label: "France (+33)" },
                { value: "+212", label: "Maroc (+212)" },
                { value: "+213", label: "Algérie (+213)" },
                { value: "+218", label: "Libye (+218)" },
                { value: "+20",  label: "Égypte (+20)" },
                { value: "+966", label: "Arabie Saoudite (+966)" },
                { value: "+971", label: "EAU (+971)" },
                { value: "+49",  label: "Allemagne (+49)" },
                { value: "+39",  label: "Italie (+39)" },
                { value: "+34",  label: "Espagne (+34)" },
                { value: "+44",  label: "Royaume-Uni (+44)" },
                { value: "+1",   label: "États-Unis (+1)" },
                { value: "+90",  label: "Turquie (+90)" },
              ] },
            ].map((f, i) => (
              <div key={i} className="border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <f.icon size={14} className="text-slate-400" />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{f.label}</p>
                </div>
                <select value={f.val} onChange={e => f.handler(e.target.value)} className={sel}>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bouton fixe en bas */}
      <div className="fixed bottom-6 right-8 z-50">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-6 py-3.5 bg-slate-900 dark:bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 dark:hover:bg-blue-700 shadow-lg shadow-slate-900/30 dark:shadow-blue-600/30 transition-all disabled:opacity-50">
          <Save size={16} /> {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </>
  );
}
